import type { OccasionDef } from "@/lib/occasions";

export class OccasionSelectionRequiredError extends Error {
  constructor() {
    super("__occasion_selection_required__");
    this.name = "OccasionSelectionRequiredError";
  }
}

export function isValidCheckoutOccasion(
  occasion: string | undefined,
  options: OccasionDef[],
): boolean {
  return !!occasion && options.some((option) => option.slug === occasion);
}

/** Recognise structured API validation errors for the occasion field without
 * mistaking a payment-method error for a missing occasion. */
export function isOccasionFieldApiError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const candidate = error as {
    code?: unknown;
    body?: {
      field?: unknown;
      fieldName?: unknown;
      error?: unknown;
      details?: unknown;
      errors?: unknown;
      fields?: unknown;
    };
  };
  const code = typeof candidate.code === "string" ? candidate.code.toLowerCase() : "";
  if (code.includes("occasion")) return true;
  const body = candidate.body;
  if (!body || typeof body !== "object") return false;
  if (body.field === "occasion" || body.fieldName === "occasion") return true;
  if (typeof body.error === "string" && body.error.toLowerCase().includes("occasion")) return true;
  if (
    body.details &&
    typeof body.details === "object" &&
    ((body.details as { field?: unknown }).field === "occasion" ||
      (body.details as { fieldName?: unknown }).fieldName === "occasion")
  ) return true;
  for (const fields of [body.errors, body.fields]) {
    if (fields && typeof fields === "object" && "occasion" in fields) return true;
    if (Array.isArray(fields) && fields.some((field) =>
      !!field && typeof field === "object" &&
      ((field as { field?: unknown }).field === "occasion" || (field as { fieldName?: unknown }).fieldName === "occasion"),
    )) return true;
  }
  return false;
}

export function isOccasionSelectionError(error: unknown): boolean {
  return error instanceof OccasionSelectionRequiredError || isOccasionFieldApiError(error);
}